import { Node, nodeInputRule } from '@tiptap/core';
import type { IMergeFieldToken } from './mergeFieldSerialization';

/** Levitate brand blue for the merge-field pill. This package is standalone (no THEME access). */
const MERGE_FIELD_BORDER_COLOR = '#0066ff';

function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export interface IMergeFieldOptions {
	/**
	 * Tokens the editor recognizes as the user types: finishing a known `{{...}}` converts it to a
	 * pill (value-mode -> `data-placeholder` span, raw-mode -> marker span). Empty by default, so the
	 * node is inert unless a consumer opts in via `MergeField.configure({ tokens })`.
	 */
	tokens: IMergeFieldToken[];
}

export interface IMergeFieldAttrs {
	/** Display label, e.g. 'first name' or 'Meeting Day'. */
	text: string;
	/**
	 * Span-mode: placeholder path, e.g. 'contact.firstName'. Serializes to a
	 * `data-placeholder` span (the legacy generic merge-field contract).
	 */
	value?: string;
	/**
	 * Raw-mode: the literal `{{...}}` token to serialize verbatim, e.g. '{{meeting day}}'.
	 * Used where the backend resolves bare braces at send-time (meeting notes) rather than
	 * `data-placeholder` spans. Takes precedence over `value` on serialization.
	 */
	symbol?: string;
}

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		mergeField: {
			insertMergeField: (attrs: IMergeFieldAttrs) => ReturnType;
		};
	}
}

/**
 * Inline atom matching the legacy placeholder span exactly in attributes:
 * `<span contenteditable="false" data-id="<uuid>" data-placeholder="true"
 * data-placeholder-value="contact.firstName">first name</span>` — so v2 notes stay
 * readable by the legacy plugin and packages/main.
 */
export const MergeField = Node.create<IMergeFieldOptions>({
	name: 'mergeField',
	group: 'inline',
	inline: true,
	atom: true,

	addOptions() {
		return { tokens: [] };
	},

	addAttributes() {
		return {
			id: { default: () => crypto.randomUUID(), parseHTML: (el: HTMLElement) => el.getAttribute('data-id') },
			text: { default: '', parseHTML: (el: HTMLElement) => el.textContent ?? '' },
			value: { default: '', parseHTML: (el: HTMLElement) => el.getAttribute('data-placeholder-value') ?? '' },
			symbol: { default: '', parseHTML: (el: HTMLElement) => el.getAttribute('data-merge-symbol') ?? '' },
		};
	},

	parseHTML() {
		// `data-merge-symbol` is the raw-mode marker the consumer stamps when pre-parsing stored
		// `{{...}}` text into pills; `data-placeholder` is the legacy span-mode contract.
		return [{ tag: 'span[data-merge-symbol]' }, { tag: 'span[data-placeholder="true"]' }];
	},

	renderHTML({ node }) {
		// Raw-mode: emit a stable marker span carrying the literal token. ProseMirror's toDOM only
		// accepts element specs (not a bare string/Text node), so the final unwrap to bare `{{...}}`
		// text happens in the consumer via serializeMergeFieldTokens (symmetric with the load parse).
		if (node.attrs.symbol) {
			return ['span', { 'data-merge-symbol': node.attrs.symbol }, node.attrs.text];
		}
		return [
			'span',
			{
				contenteditable: 'false',
				'data-id': node.attrs.id,
				'data-placeholder': 'true',
				'data-placeholder-value': node.attrs.value,
			},
			node.attrs.text,
		];
	},

	/**
	 * Editor-only rendering: show the placeholder as a dashed pill (matching the main app's merge-field
	 * styling) so it reads as a token. Serialization still uses the clean `renderHTML` span above, so the
	 * saved document stays byte-for-byte the legacy contract.
	 */
	addNodeView() {
		return ({ node }) => {
			const dom = document.createElement('span');
			dom.contentEditable = 'false';
			dom.setAttribute('data-id', node.attrs.id);
			dom.setAttribute('data-placeholder', 'true');
			dom.setAttribute('data-placeholder-value', node.attrs.value);
			if (node.attrs.symbol) {
				dom.setAttribute('data-merge-symbol', node.attrs.symbol);
			}
			dom.textContent = node.attrs.text;
			dom.style.cssText = `border:1px dashed ${MERGE_FIELD_BORDER_COLOR};border-radius:4px;padding:0 4px;margin:0 1px;white-space:nowrap;`;
			return { dom };
		};
	},

	addCommands() {
		return {
			insertMergeField:
				(attrs: IMergeFieldAttrs) =>
				({ commands }) =>
					commands.insertContent({ attrs: { ...attrs, id: crypto.randomUUID() }, type: this.name }),
		};
	},

	addInputRules() {
		const tokens = this.options.tokens;
		if (!tokens?.length) {
			return [];
		}
		const tokenBySymbol = new Map(tokens.map(t => [t.symbol, t]));
		// Longest-first so a token that is a prefix of another can't win the shorter match.
		const pattern = tokens
			.map(t => t.symbol)
			.sort((a, b) => b.length - a.length)
			.map(escapeRegExp)
			.join('|');
		return [
			nodeInputRule({
				find: new RegExp(`(${pattern})$`),
				type: this.type,
				getAttributes: match => {
					const token = tokenBySymbol.get(match[1]);
					if (!token) {
						return null;
					}
					// Generate the id here: tiptap caches a factory `default` once for the create path, so
					// relying on it would give every typed token the same data-id.
					const id = crypto.randomUUID();
					return token.value
						? { id, text: token.text, value: token.value }
						: { id, text: token.text, symbol: token.symbol };
				},
			}),
		];
	},
});
